import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Award,
  ChevronUp,
  ChevronDown,
  AlertTriangle
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useBelts } from '../../contexts/BeltContext';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Color picker component
const ColorPicker = ({ value, onChange, label }) => (
  <div className="flex flex-wrap items-center gap-2">
    <input
      type="color"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-8 h-8 rounded cursor-pointer border border-gray-300 dark:border-gray-600"
    />
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="#000000"
      className="w-24 font-mono text-sm"
    />
    {label && <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>}
  </div>
);

// Belt preview component
const BeltPreview = ({ colors, borderColor, stripeColor, isBlackBelt, degree, maxStripes }) => {
  const generateGradient = () => {
    if (!colors || colors.length === 0) return '#808080';
    if (colors.length === 1) return colors[0].color;
    if (colors.length === 2 && colors[0].color === colors[1].color) {
      return colors[0].color;
    }
    const stops = colors.map(c => `${c.color} ${c.position * 100}%`);
    return `linear-gradient(to right, ${stops.join(', ')})`;
  };

  const stripesToShow = isBlackBelt ? degree : Math.min(maxStripes || 4, 4);

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="h-8 w-36 rounded-sm flex items-center justify-end pr-3 shadow-sm"
        style={{
          background: generateGradient(),
          border: `2px solid ${borderColor}`,
        }}
      >
        {stripesToShow > 0 && (
          <div className="flex gap-1">
            {Array.from({ length: stripesToShow }).map((_, i) => (
              <div
                key={i}
                className="h-6 w-1.5 rounded-sm"
                style={{ backgroundColor: stripeColor }}
              />
            ))}
          </div>
        )}
      </div>
      <span className="text-xs text-gray-500 dark:text-gray-400">Preview</span>
    </div>
  );
};

// Color stop editor for gradients
const ColorStopEditor = ({ colorStops, onChange }) => {
  const addColorStop = () => {
    const newPosition = colorStops.length > 0
      ? Math.min(colorStops[colorStops.length - 1].position + 0.1, 1)
      : 0;
    onChange([...colorStops, { color: '#808080', position: newPosition }]);
  };

  const updateStop = (index, field, value) => {
    const updated = [...colorStops];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeStop = (index) => {
    if (colorStops.length > 1) {
      onChange(colorStops.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Color Stops</Label>
        <Button type="button" variant="outline" size="sm" onClick={addColorStop}>
          <Plus className="h-3 w-3 mr-1" /> Add Stop
        </Button>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {colorStops.map((stop, idx) => (
          <div key={idx} className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded">
            <input
              type="color"
              value={stop.color}
              onChange={(e) => updateStop(idx, 'color', e.target.value)}
              className="w-6 h-6 rounded cursor-pointer"
            />
            <Input
              value={stop.color}
              onChange={(e) => updateStop(idx, 'color', e.target.value)}
              className="w-20 font-mono text-xs"
            />
            <Input
              type="number"
              min="0"
              max="100"
              value={Math.round(stop.position * 100)}
              onChange={(e) => updateStop(idx, 'position', parseInt(e.target.value) / 100)}
              className="w-16 text-xs"
            />
            <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">%</span>
            {colorStops.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeStop(idx)}
                className="h-6 w-6 p-0 text-red-500"
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        For solid colors, use one stop. For gradients, add multiple stops at different positions (0-100%).
      </p>
    </div>
  );
};

// Belt form modal
const BeltForm = ({ belt, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    slug: belt?.slug || '',
    display_name: belt?.display_name || '',
    short_name: belt?.short_name || '',
    order: belt?.order ?? 0,
    colors: belt?.colors || [{ color: '#FFFFFF', position: 0 }, { color: '#FFFFFF', position: 1 }],
    border_color: belt?.border_color || '#000000',
    stripe_color: belt?.stripe_color || '#000000',
    text_color: belt?.text_color || '#000000',
    is_black_belt: belt?.is_black_belt || false,
    degree: belt?.degree || null,
    max_stripes: belt?.max_stripes ?? 4,
    is_active: belt?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.slug || !formData.display_name) {
      alert('Slug and Display Name are required');
      return;
    }

    setSaving(true);
    try {
      const url = belt?.id
        ? `${API_BASE}/api/admin/belts/${belt.id}`
        : `${API_BASE}/api/admin/belts`;
      const method = belt?.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save belt');
      }

      const saved = await response.json();
      onSave(saved);
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Preview */}
      <div className="flex justify-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
        <BeltPreview
          colors={formData.colors}
          borderColor={formData.border_color}
          stripeColor={formData.stripe_color}
          isBlackBelt={formData.is_black_belt}
          degree={formData.degree}
          maxStripes={formData.max_stripes}
        />
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="slug">Slug (unique identifier)</Label>
          <Input
            id="slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            placeholder="yellow_stripe"
            className="mt-1"
            disabled={!!belt?.id}
          />
        </div>
        <div>
          <Label htmlFor="order">Order</Label>
          <Input
            id="order"
            type="number"
            value={formData.order}
            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="display_name">Display Name</Label>
          <Input
            id="display_name"
            value={formData.display_name}
            onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
            placeholder="Yellow/White Stripe Belt"
            className="mt-1"
          />
        </div>
        <div>
          <Label htmlFor="short_name">Short Name</Label>
          <Input
            id="short_name"
            value={formData.short_name}
            onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
            placeholder="Yellow Stripe"
            className="mt-1"
          />
        </div>
      </div>

      {/* Color Stops */}
      <div className="border dark:border-gray-700 rounded-lg p-4">
        <ColorStopEditor
          colorStops={formData.colors}
          onChange={(colors) => setFormData({ ...formData, colors })}
        />
      </div>

      {/* Additional Colors */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Border Color</Label>
          <div className="mt-1">
            <ColorPicker
              value={formData.border_color}
              onChange={(v) => setFormData({ ...formData, border_color: v })}
            />
          </div>
        </div>
        <div>
          <Label>Stripe Color</Label>
          <div className="mt-1">
            <ColorPicker
              value={formData.stripe_color}
              onChange={(v) => setFormData({ ...formData, stripe_color: v })}
            />
          </div>
        </div>
        <div>
          <Label>Text Color</Label>
          <div className="mt-1">
            <ColorPicker
              value={formData.text_color}
              onChange={(v) => setFormData({ ...formData, text_color: v })}
            />
          </div>
        </div>
      </div>

      {/* Options */}
      <div className="grid grid-cols-3 gap-4">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_black_belt"
            checked={formData.is_black_belt}
            onChange={(e) => setFormData({
              ...formData,
              is_black_belt: e.target.checked,
              max_stripes: e.target.checked ? 0 : 4,
            })}
            className="rounded"
          />
          <Label htmlFor="is_black_belt">Black Belt</Label>
        </div>

        {formData.is_black_belt && (
          <div>
            <Label htmlFor="degree">Degree (Dan)</Label>
            <Input
              id="degree"
              type="number"
              min="1"
              max="10"
              value={formData.degree || ''}
              onChange={(e) => setFormData({ ...formData, degree: parseInt(e.target.value) || null })}
              className="mt-1"
            />
          </div>
        )}

        {!formData.is_black_belt && (
          <div>
            <Label htmlFor="max_stripes">Max Stripes</Label>
            <Input
              id="max_stripes"
              type="number"
              min="0"
              max="10"
              value={formData.max_stripes}
              onChange={(e) => setFormData({ ...formData, max_stripes: parseInt(e.target.value) || 0 })}
              className="mt-1"
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="rounded"
          />
          <Label htmlFor="is_active">Active</Label>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving}>
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : (belt?.id ? 'Update Belt' : 'Create Belt')}
        </Button>
      </div>
    </form>
  );
};

// Main BeltEditor component
const BeltEditor = () => {
  const { refreshBelts } = useBelts();
  const [belts, setBelts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBelt, setEditingBelt] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchBelts = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/belts`, {
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        const data = await response.json();
        setBelts(data);
      }
    } catch (error) {
      console.error('Error fetching belts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBelts();
  }, [fetchBelts]);

  const handleSave = async (savedBelt) => {
    setShowForm(false);
    setEditingBelt(null);
    await fetchBelts();
    await refreshBelts();
  };

  const handleDelete = async (belt) => {
    if (!confirm(`Delete "${belt.display_name}"? This cannot be undone.`)) return;

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/belts/${belt.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete belt');
      }

      await fetchBelts();
      await refreshBelts();
    } catch (error) {
      alert(error.message);
    }
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newBelts = [...belts];
    const temp = newBelts[index].order;
    newBelts[index].order = newBelts[index - 1].order;
    newBelts[index - 1].order = temp;
    [newBelts[index], newBelts[index - 1]] = [newBelts[index - 1], newBelts[index]];
    await saveOrder(newBelts);
  };

  const handleMoveDown = async (index) => {
    if (index === belts.length - 1) return;
    const newBelts = [...belts];
    const temp = newBelts[index].order;
    newBelts[index].order = newBelts[index + 1].order;
    newBelts[index + 1].order = temp;
    [newBelts[index], newBelts[index + 1]] = [newBelts[index + 1], newBelts[index]];
    await saveOrder(newBelts);
  };

  const saveOrder = async (newBelts) => {
    setBelts(newBelts);
    try {
      await adminFetch(`${API_BASE}/api/admin/belts/reorder`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          belt_orders: newBelts.map((b, i) => ({ id: b.id, order: i }))
        }),
      });
      await refreshBelts();
    } catch (error) {
      console.error('Error reordering belts:', error);
      fetchBelts();
    }
  };

  const generateGradient = (colors) => {
    if (!colors || colors.length === 0) return '#808080';
    if (colors.length === 1) return colors[0].color;
    if (colors.length === 2 && colors[0].color === colors[1].color) {
      return colors[0].color;
    }
    const stops = colors.map(c => `${c.color} ${c.position * 100}%`);
    return `linear-gradient(to right, ${stops.join(', ')})`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-500 dark:text-gray-400">
          Loading belt configurations...
        </CardContent>
      </Card>
    );
  }

  if (showForm) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            {editingBelt ? 'Edit Belt' : 'Add New Belt'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <BeltForm
            belt={editingBelt}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditingBelt(null); }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Belt Configuration"
        description="Customize belt ranks, colors, and progression order"
        icon={<Award className="h-6 w-6" />}
        actions={
          <Button onClick={() => { setEditingBelt(null); setShowForm(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Add Belt
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6">
        {belts.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <Award className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No belts configured. Run the migration script or add belts manually.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {belts.map((belt, index) => (
              <div
                key={belt.id}
                className={`flex items-center gap-4 p-3 rounded-lg border ${
                  belt.is_active ? 'bg-background' : 'bg-gray-50 dark:bg-gray-800 opacity-60'
                }`}
              >
                {/* Reorder buttons */}
                <div className="flex flex-col gap-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                  >
                    <ChevronUp className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 w-5 p-0"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === belts.length - 1}
                  >
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </div>

                {/* Belt preview */}
                <div
                  className="h-6 w-20 rounded-sm flex items-center justify-end pr-2"
                  style={{
                    background: generateGradient(belt.colors),
                    border: `2px solid ${belt.border_color}`,
                  }}
                >
                  {belt.is_black_belt && belt.degree > 0 && (
                    <div className="flex gap-0.5">
                      {Array.from({ length: Math.min(belt.degree, 5) }).map((_, i) => (
                        <div
                          key={i}
                          className="h-4 w-1 rounded-sm"
                          style={{ backgroundColor: belt.stripe_color }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Belt info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{belt.display_name}</span>
                    {belt.is_black_belt && (
                      <span className="px-1.5 py-0.5 bg-gray-900 text-white text-xs rounded">
                        {belt.degree}D
                      </span>
                    )}
                    {!belt.is_active && (
                      <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 text-xs rounded flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    <code>{belt.slug}</code> &bull; Order: {belt.order}
                    {belt.colors.length > 2 && ' &bull; Gradient'}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setEditingBelt(belt); setShowForm(true); }}
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(belt)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BeltEditor;
